import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { LcsApiService } from '@lcs/shared/api';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store, select } from '@ngrx/store';
import { EMPTY, Observable, of } from 'rxjs';
import { catchError, concatMap, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { accountPhoneNumbersRoute, phoneNumbersRoute } from 'src/app/app-routing.module';
import { AppState } from 'src/app/app.reducer';
import { selectCurrentRoute, selectRouteParams } from 'src/app/app.selectors';
import { hasSelectedPrivilege, selectPrivileges } from 'src/app/authentication/state/authentication.selector';
import { DeletePhoneNumberDialogComponent } from 'src/app/common-components/delete-phone-number-dialog/delete-phone-number-dialog.component';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { ReleasePhoneNumberDialogComponent } from 'src/app/common-components/release-phone-number-dialog/release-phone-number-dialog.component';
import { Privileges } from 'src/app/privileges/privileges';
import { getSelectedAccountId } from 'src/app/store/select-account/select-account.selector';
import { phoneNumberRegisterAdapterActions } from '../../phone-number-register/state/phone-number-register.actions';
import { GetPhoneNumberDetail, GetPhoneNumberDetailVariables } from '../queries/generated/GetPhoneNumberDetail';
import { getPhoneNumberDetail } from '../queries/phone-number-detail.graphql';
import { PhoneNumberDetailApiService } from '../services/phone-number-detail-api.service';
import * as PhoneNumberDetailActions from './phone-number-detail.actions';
@Injectable()
export class PhoneNumberDetailEffects {
   loadPhoneNumberDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(
            PhoneNumberDetailActions.phoneNumberDetailLoad,
            PhoneNumberDetailActions.savePhoneNumberDetailSuccess,
            PhoneNumberDetailActions.phoneNumberDetailCancel
         ),
         withLatestFrom(this.store.pipe(select(selectRouteParams)), this.store.pipe(select(selectCurrentRoute))),
         concatMap(([action, params, route]) => {
            const path = route.url[0].path;
            let id: number;
            if (action.type === PhoneNumberDetailActions.savePhoneNumberDetailSuccess.type) {
               // If the effect is triggered by savePhoneNumberDetailSuccess, use the ID from its payload
               id = (action as ReturnType<typeof PhoneNumberDetailActions.savePhoneNumberDetailSuccess>).data
                  .phoneNumber.phoneNumberId;
            } else {
               id = parseInt(params.id, 10);
            }
            return this.apiService
               .query<GetPhoneNumberDetail, GetPhoneNumberDetailVariables>({
                  query: getPhoneNumberDetail,
                  variables: { id },
               })
               .pipe(
                  withLatestFrom(this.store.pipe(select(selectPrivileges))),
                  map(([phoneNumberResult, privileges]) => {
                     if (
                        !hasSelectedPrivilege(
                           Privileges.ManagePhoneNumberSettings_View,
                           phoneNumberResult.data.phoneNumber.account?.accountId ?? 0,
                           privileges
                        )
                     ) {
                        this.router.navigate(['access-denied'], { skipLocationChange: true }).then(() => {
                           this.location.replaceState(route.url.join('/'));
                        });
                        return PhoneNumberDetailActions.phoneNumberDetailAccessDenied();
                     }
                     if (path === accountPhoneNumbersRoute) {
                        return PhoneNumberDetailActions.loadPhoneNumberDetailSuccess({
                           data: phoneNumberResult.data.phoneNumber,
                        });
                     } else {
                        return PhoneNumberDetailActions.loadSystemPhoneNumberDetailSuccess({
                           data: phoneNumberResult.data.phoneNumber,
                        });
                     }
                  }),
                  catchError((error) => {
                     return of(PhoneNumberDetailActions.loadPhoneNumberDetailFailure(error));
                  })
               );
         })
      );
   });

   savePhoneNumberDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(PhoneNumberDetailActions.savePhoneNumberDetail),
         switchMap((action) => {
            const apiCall = this.phoneNumberDetailApiService.save(action.phoneNumber, action.phoneNumberFields);
            return apiCall.pipe(
               map((result) => {
                  return PhoneNumberDetailActions.savePhoneNumberDetailSuccess({ data: result.data });
               }),
               catchError((error) => {
                  return of(PhoneNumberDetailActions.savePhoneNumberDetailFailure(error));
               })
            );
         })
      );
   });

   navigate$ = createEffect(
      () =>
         this.actions$.pipe(
            ofType(PhoneNumberDetailActions.navigate),
            withLatestFrom(this.store.pipe(select(selectRouteParams)), this.store.pipe(select(selectCurrentRoute))),
            tap(([action, routeParams, currentRoute]) => {
               const path = currentRoute.url[0].path;

               if (path === accountPhoneNumbersRoute) {
                  this.router.navigateByUrl(`/${accountPhoneNumbersRoute}/${action.id}`, { replaceUrl: true });
               } else {
                  this.router.navigateByUrl(`/${phoneNumbersRoute}/${action.id}`, { replaceUrl: true });
               }
            })
         ),
      { dispatch: false }
   );

   releasePhoneNumberDetail = createEffect(() => {
      return this.actions$.pipe(
         ofType(PhoneNumberDetailActions.releasePhoneNumberDetail),
         switchMap((action) => {
            return this.openReleasePhoneNumberDialog(
               action.phonesUsingForCallerId ?? [],
               action.faxesUsingForCallerId ?? []
            ).pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     const apiCall = this.phoneNumberDetailApiService.ReleaseFromAccount(
                        action.phoneNumber.phoneNumberId,
                        false
                     );
                     return apiCall.pipe(
                        map((result) => {
                           return PhoneNumberDetailActions.releasePhoneNumberDetailSuccess({ data: result });
                        }),
                        catchError((error) => {
                           return of(PhoneNumberDetailActions.releasePhoneNumberDetailFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deletePhoneNumberDetailFromNDT = createEffect(() => {
      return this.actions$.pipe(
         ofType(PhoneNumberDetailActions.deletePhoneNumberDetailFromNDT),
         switchMap((action) => {
            return this.openDeletePhoneNumberDialog(
               action.phonesUsingForCallerId ?? [],
               action.faxesUsingForCallerId ?? []
            ).pipe(
               switchMap((confirmed) => {
                  if (confirmed) {
                     const apiCall = this.phoneNumberDetailApiService.ReleaseFromAccount(
                        action.phoneNumber.phoneNumberId,
                        true
                     );
                     return apiCall.pipe(
                        map((result) => {
                           return PhoneNumberDetailActions.deletePhoneNumberDetailFromNDTSuccess({ data: result });
                        }),
                        catchError((error) => {
                           return of(PhoneNumberDetailActions.deletePhoneNumberDetailFromNDTFailure(error));
                        })
                     );
                  }
                  return EMPTY;
               })
            );
         })
      );
   });

   deletePhoneNumberDetailFromNDTSuccess = createEffect(() => {
      return this.actions$.pipe(
         ofType(PhoneNumberDetailActions.deletePhoneNumberDetailFromNDTSuccess),
         withLatestFrom(this.store.pipe(select(selectCurrentRoute)), this.store.pipe(select(getSelectedAccountId))),
         concatMap(([, route, selectedAccountId]) => {
            this.snackBar.open('Phone Number Deleted');
            const isOnDetailsPage = !!route.params.id;
            if (isOnDetailsPage) {
               if (selectedAccountId) {
                  this.router.navigate([accountPhoneNumbersRoute]);
               } else {
                  this.router.navigate([phoneNumbersRoute]);
               }
               return EMPTY;
            } else {
               return of(phoneNumberRegisterAdapterActions.pageEnter());
            }
         })
      );
   });

   deletePhoneNumberDetailFromNDTFailure = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PhoneNumberDetailActions.deletePhoneNumberDetailFromNDTFailure),
            tap(() => {
               this.snackBar.open('Phone Number Not Deleted');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   savePhoneNumberDetailSuccess = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PhoneNumberDetailActions.savePhoneNumberDetailSuccess),
            tap(() => {
               this.snackBar.open('Phone Number Saved');
            })
         );
      },
      {
         dispatch: false,
      }
   );

   releasePhoneNumberDetailSuccess = createEffect(() => {
      return this.actions$.pipe(
         ofType(PhoneNumberDetailActions.releasePhoneNumberDetailSuccess),
         withLatestFrom(this.store.pipe(select(selectCurrentRoute)), this.store.pipe(select(getSelectedAccountId))),
         concatMap(([, route, selectedAccountId]) => {
            this.snackBar.open('Phone Number Released');
            const isOnDetailsPage = !!route.params.id;
            if (isOnDetailsPage) {
               if (selectedAccountId) {
                  this.router.navigate([accountPhoneNumbersRoute]);
               } else {
                  this.router.navigate([phoneNumbersRoute]);
               }
               return EMPTY;
            } else {
               return of(phoneNumberRegisterAdapterActions.pageEnter());
            }
         })
      );
   });

   verifyRoute = createEffect(
      () => {
         return this.actions$.pipe(
            ofType(PhoneNumberDetailActions.verifyRoute),
            tap((action) => {
               if (action?.accountId) {
                  this.router.navigate([accountPhoneNumbersRoute, action.phoneNumber], { replaceUrl: true });
               } else {
                  this.router.navigate([phoneNumbersRoute, action.phoneNumber], { replaceUrl: true });
               }
            })
         );
      },
      { dispatch: false }
   );

   constructor(
      private actions$: Actions,
      private apiService: LcsApiService,
      private dialogService: LcsDialogService,
      private store: Store<AppState>,
      private phoneNumberDetailApiService: PhoneNumberDetailApiService,
      private snackBar: MatSnackBar,
      private router: Router,
      private location: Location
   ) {}

   openReleasePhoneNumberDialog(phoneExtensionList: any, faxExtensionList: any): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(ReleasePhoneNumberDialogComponent, {
         closeOnNavigation: true,
         data: {
            phoneExtensionList,
            faxExtensionList,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }

   openDeletePhoneNumberDialog(phoneExtensionList: any, faxExtensionList: any): Observable<any> {
      const dialogRef = this.dialogService.openFormComponent(DeletePhoneNumberDialogComponent, {
         closeOnNavigation: true,
         data: {
            phoneExtensionList,
            faxExtensionList,
         },
         disableClose: true,
         panelClass: ['dialog-md', 'dialog-container'],
      });
      return dialogRef.afterClosed();
   }
}
